import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import {
  EmailOutlined,
  PhoneOutlined,
  LocationOnOutlined,
  Visibility,
  Share as ShareIcon,
  Laptop,
  Person,
  ArrowForward,
  KeyboardArrowRight,
  KeyboardArrowUp,
  Star,
  School,
  Work,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  Groups as GroupsIcon,
  AccessTimeOutlined
} from '@mui/icons-material';
import { FaCrown } from 'react-icons/fa';
import { getAvatarUrl } from '../../../../utils/imageUtils';
import ShareProfileDialog from '../../../../Components/Dialog/ShareProfile';
import ImageWithSkeleton from '../../../../Components/ImageWithSkeleton';
import circleContainer from '../../../../assets/images/circle-container.svg';
import SocialLinksDialog from './SocialLinksDialog';
import { formatResponseTime } from '../../../../utils/formatResponseTime';
import config from 'config';

const ModernTutorCard = ({ tutor }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const locationUrl = useLocation();
  const { userId, isGuest, status } = useSelector((state) => state.main);
  
  const [openShareProfileDialog, setOpenShareProfileDialog] = useState(false);
  const [openSocialsDialog, setOpenSocialsDialog] = useState(false);
  const [phoneRevealed, setPhoneRevealed] = useState(false);
  const [readMoreAbout, setReadMoreAbout] = useState(false);


  const {
    _id,
    user,
    avatar,
    realDistance,
    email,
    phone,
    about,
    price,
    qualifications,
    title,
    online,
    faceToFace,
    gender,
    level,
    address,
    userSubject,
    featured,
    premiumOnly,
    links,
  } = tutor;

  // Show action buttons / badges only for premium tutors. projectTutor sets
  // premiumOnly: true on Basic tutors and strips premium-only fields, so this
  // single flag drives the whole "is this a paid tutor?" answer in the UI.
  const isPremiumTutor = !premiumOnly;
  const websiteUrl = links?.website;
  const hasAnySocial = !!(
    links?.facebook ||
    links?.instagram ||
    links?.x ||
    links?.linkedin ||
    links?.whatsapp ||
    websiteUrl
  );

  const { firstName, lastName, averageResponseTime } = user || {};
  const userAvatar = getAvatarUrl(avatar, gender);

  // Normalise price so that it matches the tutor detail view
  // In many places price is stored as an array (e.g. [100, 100]),
  // where the first element is the primary hourly rate.
  const hourlyPrice = Array.isArray(price) ? price[0] : price;


  const handleShareClick = (e) => {
    e.stopPropagation();
    setOpenShareProfileDialog(true);
  };

  const handleReadMore = (e) => {
    e.stopPropagation();
    setReadMoreAbout(!readMoreAbout);
  };

  const handleCloseSharePopup = () => {
    setOpenShareProfileDialog(false);
  };

  // Format qualifications for display
  const getQualificationChips = () => {
    const chips = [];
    if (qualifications) {
      Object.entries(qualifications).forEach(([key, value]) => {
        if (value > 0 && key !== 'Student') {
          chips.push(key);
        }
      });
    }
    return chips.slice(0, 3); // Show max 3 qualifications
  };

  // Format about text - preserve HTML structure for proper rendering
  const formatAboutText = (text) => {
    if (!text) return '';
    
    // Clean up the HTML but preserve structure for lists
    let cleanText = text
      .replace(/<br\s*\/?>/gi, '<br>') // Keep <br> tags
      .replace(/<ul[^>]*>/gi, '<ul>') // Clean ul tags
      .replace(/<li[^>]*>/gi, '<li>') // Clean li tags
      .replace(/<p[^>]*>/gi, '<p>') // Clean p tags
      .replace(/<[^>]*>/g, (match) => {
        // Keep only essential HTML tags
        if (['<ul>', '</ul>', '<li>', '</li>', '<p>', '</p>', '<br>'].includes(match)) {
          return match;
        }
        return '';
      })
      .replace(/\s+/g, ' ') // Clean up extra spaces
      .trim();
    
    return cleanText;
  };

  const shouldShowReadMore = about && about.replace(/<[^>]*>/g, '').length > 200;

  const qualificationChips = getQualificationChips();

  return (
    <>
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
          overflow: 'hidden',
          mb: 3, // Add margin bottom for list spacing
          position: 'relative', // anchor for the top-left Featured chip
        }}
      >
        {/* Top-left "Featured" badge — shown for every premium tutor (no
            separate admin "featured" flag required per Nick's spec). Square
            tag look: light grey-purple fill, small star, dark purple text. */}
        {isPremiumTutor && (
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.4,
              backgroundColor: '#f5f0f7',
              border: '1px solid #e4d6ec',
              borderRadius: 1,
              zIndex: 1,
            }}
          >
            <Star sx={{ fontSize: 14, color: '#712d94' }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#4a1d62', lineHeight: 1 }}>
              Featured
            </Typography>
          </Box>
        )}
        <CardContent sx={{
          p: 0,
          '&:last-child': {
            paddingBottom: 0
          }
        }}>
          {/* Header Section — extra top padding for premium tutors so the
              absolutely-positioned "Featured" chip doesn't sit on top of the
              avatar. Basic tutors don't render the chip, so they keep the
              tighter default top padding. */}
          <Box sx={{ p: 2.5, pt: isPremiumTutor ? 5 : 2.5, pb: 1 }}>
            <Box 
              sx={{ 
                display: 'flex', 
                gap: 2, 
                alignItems: 'flex-start',
                flexDirection: { xs: 'column', sm: 'row' }
              }}
            >
              {/* Avatar */}
              <Avatar
                src={userAvatar}
                sx={{
                  width: 120,
                  height: 120,
                  mx: { xs: 'auto', sm: 0 },   // Center avatar on mobile
                  mb: { xs: 1.5, sm: 0 },      // Add spacing below avatar on mobile
                  border: `3px solid ${theme.palette.background.paper}`,
                  boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.15)}`,
                  backgroundColor: userAvatar ? 'transparent' : alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  fontSize: '2.5rem',
                  fontWeight: 600,
                  '& .MuiAvatar-img': {
                    objectFit: 'cover'
                  }
                }}
              >
                {!userAvatar && `${firstName?.[0] || ''}${lastName?.[0] || ''}`}
              </Avatar>

              {/* Main Info */}
              <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start', 
                  mb: 1,
                  gap: 2,
                  flexWrap: { xs: 'wrap', md: 'nowrap' }
                }}>
                  <Box sx={{ flex: '1 1 auto', minWidth: 0, maxWidth: { xs: '100%', md: 'calc(100% - 200px)' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.25 }}>
                      <Typography variant="h6" sx={{
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        fontSize: '1.25rem'
                      }}>
                        {firstName} {lastName && lastName[0]}.
                      </Typography>
                      {isPremiumTutor && (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1,
                            py: '5px',
                            backgroundColor: '#fff9e6',
                            border: '1px solid #f5cf60',
                            borderRadius: 1,
                          }}
                        >
                          <FaCrown size={12} color="#712d94" />
                          <Typography
                            sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#712d94', lineHeight: 1 }}
                          >
                            Premium Tutor
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        textTransform: 'capitalize',
                        mb: 0.5,
                        fontSize: '1rem'
                      }}
                    >
                      {title}
                    </Typography>

                    {/* Response time — recomputed nightly by the
                        averageResponseTime cron. Falls back to "2 hours"
                        when there's no data yet (formatter handles -1/null). */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <AccessTimeOutlined sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary, fontSize: '0.8rem' }}
                      >
                        Usually replies within {formatResponseTime(averageResponseTime)}
                      </Typography>
                    </Box>

                    {/* Contact Info - Compact (email + phone removed per spec;
                        phone is reached via the "Call Tutor" action button
                        which reveals the number inline on desktop). */}
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                      {realDistance !== undefined && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                          <LocationOnOutlined fontSize="small" sx={{ color: theme.palette.success.main, fontSize: 16 }} />
                          <Typography variant="caption" sx={{ color: theme.palette.success.main, fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                            {realDistance.toFixed(1)} miles
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: { xs: 'stretch', md: 'flex-end' }, 
                    gap: 1,
                    flexShrink: 0,
                    minWidth: { xs: '100%', md: 'auto' },
                    width: { xs: '100%', md: 'auto' }
                  }}>
                    {/* Mobile: Price at top, Desktop: Buttons at top */}
                    <Box sx={{ 
                      display: { xs: 'flex', md: 'none' },
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%'
                    }}>
                      {/* Price - Mobile */}
                      <Typography sx={{ 
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        color: '#712d94',
                        whiteSpace: 'nowrap'
                      }}>
                        £{hourlyPrice}
                        <Box component="span" sx={{ fontSize: '0.95rem', fontWeight: 600 }}> - hr</Box>
                      </Typography>
                      
                      {/* Availability - Mobile */}
                      <Stack direction="row" spacing={1.5}>
                        {online && (
                          <Chip
                            icon={<Laptop sx={{ fontSize: '0.875rem !important' }} />}
                            label="Online"
                            size="small"
                            sx={{
                              height: 24,
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              '& .MuiChip-icon': {
                                color: theme.palette.primary.main
                              }
                            }}
                          />
                        )}
                        {faceToFace && (
                          <Chip
                            icon={<Person sx={{ fontSize: '0.875rem !important' }} />}
                            label="In-person"
                            size="small"
                            sx={{
                              height: 24,
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                              color: theme.palette.secondary.main,
                              '& .MuiChip-icon': {
                                color: theme.palette.secondary.main
                              }
                            }}
                          />
                        )}
                      </Stack>
                    </Box>
                    
                    {/* Buttons Row */}
                    <Stack
                      direction="row"
                      spacing={0.5}
                      sx={{
                        justifyContent: { xs: 'space-between', md: 'flex-end' },
                        width: '100%'
                      }}
                    >
                      <Tooltip title="Share Profile">
                        <IconButton
                          size="small"
                          onClick={handleShareClick}
                          sx={{
                            color: theme.palette.text.secondary,
                            flexShrink: 0,
                            '&:hover': {
                              color: theme.palette.primary.main,
                              backgroundColor: alpha(theme.palette.primary.main, 0.08)
                            }
                          }}
                        >
                          <ShareIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => navigate(`/tutor/${_id}${locationUrl.search}`)}
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                          borderRadius: 1.5,
                          px: { xs: 2, md: 1.5 },
                          py: 0.5,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: { xs: '0.875rem', md: '0.8rem' },
                          whiteSpace: 'nowrap',
                          flex: { xs: 1, md: '0 0 auto' },
                          '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                          }
                        }}
                      >
                        View Profile →
                      </Button>
                    </Stack>
                    
                    {/* Desktop only: Action buttons (Premium tutors) + Availability + Price */}
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'row', gap: 1.5, alignItems: 'flex-start' }}>
                      {/* Premium action buttons — only render the column at
                          all if the tutor is Premium AND at least one of
                          phone / website / socials is present. Each button
                          hides itself when its data is missing. */}
                      {isPremiumTutor && (phone || websiteUrl || hasAnySocial) && (
                        <Stack spacing={0.75} sx={{ alignItems: 'stretch', minWidth: 130 }}>
                          {phone && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<PhoneIcon sx={{ fontSize: 16, color: '#712d94' }} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setPhoneRevealed(true);
                              }}
                              sx={{
                                justifyContent: 'flex-start',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                color: '#712d94',
                                borderColor: phoneRevealed ? '#712d94' : theme.palette.divider,
                                backgroundColor: phoneRevealed
                                  ? alpha(theme.palette.primary.main, 0.06)
                                  : 'transparent',
                                borderRadius: 1.5,
                                py: 0.4,
                                px: 1.25,
                                '&:hover': {
                                  borderColor: '#712d94',
                                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                },
                              }}
                            >
                              {phoneRevealed ? phone : 'Call Tutor'}
                            </Button>
                          )}
                          {websiteUrl && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<LanguageIcon sx={{ fontSize: 16, color: '#712d94' }} />}
                              href={websiteUrl.match(/^https?:\/\//) ? websiteUrl : `//${websiteUrl}`}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              sx={{
                                justifyContent: 'flex-start',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                color: '#712d94',
                                borderColor: theme.palette.divider,
                                borderRadius: 1.5,
                                py: 0.4,
                                px: 1.25,
                                '&:hover': {
                                  borderColor: '#712d94',
                                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                },
                              }}
                            >
                              Website
                            </Button>
                          )}
                          {hasAnySocial && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<GroupsIcon sx={{ fontSize: 16, color: '#712d94' }} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenSocialsDialog(true);
                              }}
                              sx={{
                                justifyContent: 'flex-start',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                color: '#712d94',
                                borderColor: theme.palette.divider,
                                borderRadius: 1.5,
                                py: 0.4,
                                px: 1.25,
                                '&:hover': {
                                  borderColor: '#712d94',
                                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                },
                              }}
                            >
                              Socials
                            </Button>
                          )}
                        </Stack>
                      )}

                      {/* Availability + Price column */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
                        {/* Availability Icons with Labels */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-end' }}>
                          {online && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Laptop fontSize="small" sx={{ color: theme.palette.primary.main, fontSize: 14 }} />
                              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#1a1a1a', whiteSpace: 'nowrap' }}>
                                Online
                              </Typography>
                            </Box>
                          )}
                          {faceToFace && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Person fontSize="small" sx={{ color: theme.palette.secondary.main, fontSize: 14 }} />
                              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#1a1a1a', whiteSpace: 'nowrap' }}>
                                In-person
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Price - Desktop */}
                        <Typography sx={{
                          fontSize: '1rem',
                          fontWeight: 700,
                          color: '#712d94',
                          mt: 0.5,
                          whiteSpace: 'nowrap'
                        }}>
                          £{hourlyPrice}
                          <Box component="span" sx={{ fontSize: '0.85rem', fontWeight: 600 }}> - hr</Box>
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Divider />

          {/* About Section */}
          <Box sx={{ p: 2.5, pt: 1.5, pb: 1 }}>
            {/* Text Container with Overlay */}
            <Box sx={{ position: 'relative', mb: shouldShowReadMore && !readMoreAbout ? 0 : 0 }}>
              <Box sx={{ position: 'relative' }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#1a1a1a', 
                    lineHeight: 1.6,
                    fontSize: '0.95rem',
                    // When collapsed, clamp to 3 lines using webkit line-clamp for
                    // consistent truncation across different HTML content.
                    ...(readMoreAbout
                      ? {}
                      : {
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }),
                    '& ul': {
                      margin: 0,
                      paddingLeft: 0,
                      listStyleType: 'none',
                      fontSize: '0.95rem'
                    },
                    '& li': {
                      marginBottom: 0.5,
                      position: 'relative',
                      paddingLeft: '1.2em',
                      fontSize: '0.95rem',
                      '&::before': {
                        content: '"•"',
                        position: 'absolute',
                        left: 0,
                        color: '#444',
                        fontSize: '0.95rem'
                      }
                    },
                    '& p': {
                      // Remove margins so paragraph spacing doesn't consume the
                      // clamped height and prematurely cut the content.
                      margin: 0,
                      fontSize: '0.95rem'
                    }
                  }}
                  dangerouslySetInnerHTML={{ 
                    __html: formatAboutText(about)
                  }}
                />
                {/* Gradient Overlay when collapsed */}
                {shouldShowReadMore && !readMoreAbout && (
                  <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '40px',
                    background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.6) 40%, rgba(255,255,255,1) 100%)',
                    pointerEvents: 'none',
                    zIndex: 1
                  }} />
                )}
              </Box>
            </Box>
            
            {/* View More/Less Button */}
            {shouldShowReadMore && (
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'center',
                mt: readMoreAbout ? 1 : 0.5
              }}>
                <Typography
                  component="span"
                  onClick={handleReadMore}
                  sx={{
                    color: '#9b6bb3',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    fontWeight: 600,
                    textDecoration: 'none',
                    position: 'relative',
                    zIndex: 2,
                    backgroundColor: '#fff',
                    px: 1,
                    '&:hover': {
                      color: theme.palette.primary.main
                    }
                  }}
                >
                  {readMoreAbout ? 'View Less' : 'View More'}
                </Typography>
              </Box>
            )}
          </Box>

          <Divider />

          {/* Bottom Section */}
          <Box sx={{ p: 2.5, pt: 1 }}>
            {/* Tutoring Info */}
            <Box>
              
              {/* Qualification Chips */}
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {qualificationChips.map((qual, index) => (
                  <Chip
                    key={index}
                    label={qual}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: '0.75rem',
                      height: 22,
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      color: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      fontWeight: 500,
                      '& .MuiChip-label': {
                        px: 0.75
                      }
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Share Profile Dialog */}
      <ShareProfileDialog
        open={openShareProfileDialog}
        handleClose={handleCloseSharePopup}
        tutorId={_id}
        tutorName={`${firstName} ${lastName && lastName[0]}.`}
      />

      {/* Social Links Dialog — opened by the "Socials" action button */}
      <SocialLinksDialog
        open={openSocialsDialog}
        onClose={() => setOpenSocialsDialog(false)}
        links={links}
      />
    </>
  );
};

export default ModernTutorCard;
